import React, { useState, useMemo, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  CircularProgress,
  Typography,
  TableSortLabel,
  IconButton,
  Collapse,
  Button,
  MenuItem,
  Checkbox,
  ListItemText,
  FormControl,
  InputLabel,
  Select,
  Chip,
  Autocomplete,
  TextField,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { KeyboardArrowDown, KeyboardArrowUp, FilterList, ArrowUpward, ArrowDownward } from '@mui/icons-material';
import { PredictionResponse, DriverDetails } from '../types/predictions';
import { DriverDetailsModal } from './DriverDetailsModal';
import { OddsToggle, toAmericanOdds } from './OddsToggle';
import { COLORS, interpolateColor, getCellColor } from '../utils/cellColors';
import { getBadgeColor, statLabels, StatKey } from '../utils/statBadges';
import { useQuery } from '@tanstack/react-query';
import { getDriverDetails, DriverDetailResponse, getLoopData, LoopData } from '../services/api';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  horizontalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { SelectChangeEvent } from '@mui/material/Select';

interface PredictionResultsProps {
  predictions: PredictionResponse[];
  isLoading: boolean;
}

type SortField = 'rank' | 'driverName' | 'predictedPosition' | 'winProbability' | 'top5Probability' | 'top10Probability';
type SortOrder = 'asc' | 'desc';

interface DriverSelection {
  driverName: string;
  trackNames: string[];
  year: number;
}

type DriverDetailsMap = Record<string, DriverDetails[]>;

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

// Add color mapping for loop data fields
const loopDataColorMapping: Record<string, { type: 'position' | 'probability' | 'rating' | 'count' | 'differential', invert?: boolean }> = {
  St: { type: 'position'},
  DriverRating: { type: 'rating' },
  Mid: { type: 'position' },
  High: { type: 'position' },
  Low: { type: 'position' },
  Avg: { type: 'position' },
  PassDif: { type: 'rating' },
  GfPass: { type: 'rating' },
  GfPassed: { type: 'rating', invert: true },
  QPass: { type: 'rating' },
  PctQPass: { type: 'probability' },
  Fastest: { type: 'rating' },
  T15Lap: { type: 'rating' },
  PctT15Lap: { type: 'probability' },
  Led: { type: 'rating' },
  PctLed: { type: 'probability' },
  Total: { type: 'rating' },
  Pos: { type: 'position' },
  WinPct: { type: 'probability' },
  Top5Pct: { type: 'probability' },
  Top10Pct: { type: 'probability' },
  Wins: { type: 'count' },
  Top5s: { type: 'count' },
  Top10s: { type: 'count' },
  StPosDiff: { type: 'differential' },
};

// Column definitions for the loop data table
const defaultLoopDataColumns = [
  { id: 'Driver', label: 'Driver' },
  { id: 'Pos', label: 'Avg\nFinish' },
  { id: 'DriverRating', label: 'Driver\nRating' },
  { id: 'Avg', label: 'Avg\nRunning\nPos' },
  { id: 'PctT15Lap', label: 'Top 15\n%' },
  { id: 'Fastest', label: 'Fastest\nLaps' },
  { id: 'Wins', label: 'Wins' },
  { id: 'WinPct', label: 'Win %' },
  { id: 'Top5s', label: 'Top 5s' },
  { id: 'Top5Pct', label: 'Top 5 %' },
  { id: 'Top10s', label: 'Top 10s' },
  { id: 'Top10Pct', label: 'Top 10 %' },
  { id: 'St', label: 'Start' },
  { id: 'StPosDiff', label: 'St-Pos\nDiff' },
  { id: 'Mid', label: 'Mid' },
  { id: 'High', label: 'High' },
  { id: 'Low', label: 'Low' },
  { id: 'PassDif', label: 'Pass\n+/-' },
  { id: 'GfPass', label: 'GF\nPasses' },
  { id: 'GfPassed', label: 'GF\nPassed' },
  { id: 'QPass', label: 'Quality\nPasses' },
  { id: 'PctQPass', label: 'Quality\nPass %' },
  { id: 'T15Lap', label: 'Top 15\nLaps' },
  { id: 'Led', label: 'Laps\nLed' },
  { id: 'PctLed', label: 'Led\n%' },
  { id: 'Total', label: 'Total\nLaps' },
];

// Add SortableHeader component
const SortableHeader: React.FC<{ 
  column: typeof defaultLoopDataColumns[0];
  sortField: string;
  sortDirection: 'asc' | 'desc';
  onSort: (columnId: string) => void;
  isMobile: boolean;
}> = ({ column, sortField, sortDirection, onSort, isMobile }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: column.id });

  const isDriverColumn = column.id === 'Driver';
  const dragStyle = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  
  const muiStyle = {
    fontWeight: 'bold',
    whiteSpace: 'pre-line',
    padding: '8px 16px',
    ...(isMobile && isDriverColumn ? {
      position: 'sticky',
      left: '30px', // After the Rank column (30px wide on mobile)
      zIndex: 4,
      backgroundColor: 'background.paper',
      borderRight: '1px solid rgba(224, 224, 224, 1)',
    } : {}),
    '&:hover': {
      backgroundColor: 'action.hover',
    },
  };

  const handleSortClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onSort(column.id);
  };

  return (
    <TableCell 
      ref={setNodeRef} 
      sx={muiStyle}
      style={dragStyle}
      {...attributes} 
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          userSelect: 'none',
          width: '100%',
          height: '100%',
        }}
      >
        {/* Main draggable area with column label */}
        <Box
          {...listeners}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: column.id === 'Driver' ? 'flex-start' : 'flex-end',
            flex: 1,
            cursor: 'grab',
            minHeight: '100%',
            '&:active': {
              cursor: 'grabbing',
            },
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.05)',
            },
          }}
        >
          {column.label}
        </Box>
        
        {/* Small sort icon area */}
        <Box
          onClick={handleSortClick}
          sx={{
            width: '20px',
            height: '20px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '3px',
            ml: 0.5,
            opacity: 0.6,
            '&:hover': {
              opacity: 1,
              backgroundColor: 'rgba(0, 0, 0, 0.08)',
            },
          }}
        >
          {sortField === column.id ? (
            // Show sort direction indicator with subtle green color
            sortDirection === 'asc' ? 
              <ArrowUpward fontSize="small" sx={{ fontSize: '16px', color: '#4caf50', opacity: 0.8 }} /> : 
              <ArrowDownward fontSize="small" sx={{ fontSize: '16px', color: '#4caf50', opacity: 0.8 }} />
          ) : (
            // Show neutral sort icon
            <FilterList fontSize="small" sx={{ fontSize: '16px', color: 'text.secondary' }} />
          )}
        </Box>
      </Box>
    </TableCell>
  );
};

export const PredictionResults: React.FC<PredictionResultsProps> = ({ predictions, isLoading }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [orderBy, setOrderBy] = useState<SortField>('predictedPosition');
  const [order, setOrder] = useState<SortOrder>('asc');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [selectedDrivers, setSelectedDrivers] = useState<DriverSelection[]>([]);
  const [visibleDrivers, setVisibleDrivers] = useState<Set<string>>(new Set());
  const [showAmericanOdds, setShowAmericanOdds] = useState<boolean>(false);
  // The Predictions tab is hidden: the page only shows Loop Data (tab 1).
  const [tabValue] = useState(1);
  const [loopData, setLoopData] = useState<LoopData[]>([]);
  const [loopDataLoading, setLoopDataLoading] = useState(false);
  const [loopDataColumns, setLoopDataColumns] = useState(defaultLoopDataColumns);
  const [loopDataSort, setLoopDataSort] = useState<string>('DriverRating');
  const [loopDataSortDirection, setLoopDataSortDirection] = useState<'asc' | 'desc'>('desc');
  const [checkedDrivers, setCheckedDrivers] = useState<Set<string>>(new Set());
  const [minRacesFilter, setMinRacesFilter] = useState<number>(0);

  const sensors = useSensors(useSensor(PointerSensor));

  // Get unique tracks from predictions - now it will be a comma-separated list
  const uniqueTracks = useMemo(() => {
    const trackSets = predictions.map(p => new Set(p.trackName.split(", ")));
    return Array.from(new Set(trackSets.flatMap(set => Array.from(set))));
  }, [predictions]);

  // Get all driver names from predictions
  const allDrivers = useMemo(() => {
    return Array.from(new Set(predictions.map(p => p.driverName))).sort((a, b) => a.localeCompare(b));
  }, [predictions]);

  // Initialize visible drivers when predictions change
  useEffect(() => {
    setVisibleDrivers(new Set(allDrivers));
  }, [allDrivers]);

  // Add this effect to fetch loop data when predictions change
  useEffect(() => {
    if (predictions.length > 0) {
      const fetchLoopData = async () => {
        setLoopDataLoading(true);
        try {
          const request = {
            trackNames: predictions[0].trackName.split(", "),
            year: predictions[0].year,
          };
          const data = await getLoopData(request);
          setLoopData(data);
        } catch (error) {
          console.error('Error fetching loop data:', error);
        } finally {
          setLoopDataLoading(false);
        }
      };
      fetchLoopData();
    }
  }, [predictions]);

  const { data: driversDetails, isLoading: isDriverDetailsLoading } = useQuery<DriverDetailsMap>({
    queryKey: ['driverDetails', selectedDrivers],
    queryFn: async () => {
      if (selectedDrivers.length === 0) return {};
      
      // Get unique track/year combinations
      const uniqueCombos = selectedDrivers.reduce((acc, curr) => {
        const key = `${curr.trackNames.join('-')}-${curr.year}`;
        if (!acc[key]) {
          acc[key] = {
            trackNames: curr.trackNames,
            year: curr.year,
            drivers: new Set<string>(),
          };
        }
        acc[key].drivers.add(curr.driverName);
        return acc;
      }, {} as Record<string, { trackNames: string[]; year: number; drivers: Set<string> }>);

      // Fetch details for each unique track/year combination
      const detailsPromises = Object.values(uniqueCombos).map(({ trackNames, year, drivers }) =>
        getDriverDetails(Array.from(drivers), trackNames, year)
      );
      
      const results = await Promise.all(detailsPromises);
      
      // Combine all results and group by driver
      const groupedData: DriverDetailsMap = {};
      results.flat().forEach((detail: DriverDetailResponse) => {
        const driverName = detail.Driver;
        if (!groupedData[driverName]) {
          groupedData[driverName] = [];
        }
        const { Driver, ...raceDetails } = detail;
        groupedData[driverName].push(raceDetails);
      });

      return groupedData;
    },
    enabled: selectedDrivers.length > 0,
  });

  const handleExpandClick = (driverName: string) => {
    setExpanded(prev => {
      const newSet = new Set(prev);
      if (newSet.has(driverName)) {
        newSet.delete(driverName);
      } else {
        newSet.add(driverName);
      }
      return newSet;
    });
  };

  const handleCollapseAll = () => {
    setExpanded(new Set());
    setSelectedDrivers([]);
  };

  const handleExpandedRowClick = (driver: PredictionResponse) => {
    setSelectedDrivers(prev => {
      const existingDrivers = prev.filter(d => d.driverName !== driver.driverName);
      const expandedDrivers = Array.from(expanded).filter(
        dName => dName !== driver.driverName && !prev.some(d => d.driverName === dName)
      );
      
      return [
        ...existingDrivers,
        ...expandedDrivers.map(dName => {
          const driverData = predictions.find(p => p.driverName === dName)!;
          return {
            driverName: dName,
            trackNames: uniqueTracks,
            year: driverData.year,
          };
        }),
        {
          driverName: driver.driverName,
          trackNames: uniqueTracks,
          year: driver.year,
        },
      ];
    });
  };

  const handleCloseModal = () => {
    setSelectedDrivers([]);
  };

  const handleRequestSort = (property: SortField) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Sort and filter predictions
  const sortedAndFilteredPredictions = useMemo(() => {
    return [...predictions]
      .filter(prediction => visibleDrivers.has(prediction.driverName))
      .sort((a, b) => {
        let comparison = 0;
        switch (orderBy) {
          case 'rank':
            comparison = 0; // Rank is already sorted by index
            break;
          case 'driverName':
            comparison = a.driverName.localeCompare(b.driverName);
            break;
          case 'predictedPosition':
            comparison = a.predictedPosition - b.predictedPosition;
            break;
          case 'winProbability':
            comparison = a.winProbability - b.winProbability;
            break;
          case 'top5Probability':
            comparison = a.top5Probability - b.top5Probability;
            break;
          case 'top10Probability':
            comparison = a.top10Probability - b.top10Probability;
            break;
        }
        return order === 'asc' ? comparison : -comparison;
      });
  }, [predictions, order, orderBy, visibleDrivers]);





  // Function to get cell color based on value and field type
  const getLoopDataCellColor = (value: number, field: string) => {
    const mapping = loopDataColorMapping[field];
    if (!mapping) return undefined;

    let normalizedValue: number;
    switch (mapping.type) {
      case 'position':
        // For positions: 1 (green) to 40 (red)
        normalizedValue = Math.min(Math.max(value, 1), 40);
        normalizedValue = mapping.invert ? 
          (normalizedValue - 1) / 39 : // 39 is the range (40 - 1)
          1 - ((normalizedValue - 1) / 39);
        break;
      case 'probability':
        // For probabilities: 0 (red) to 100 (green)
        normalizedValue = Math.min(Math.max(value, 0), 100) / 100;
        // Return red-yellow-green gradient for percentage columns
        if (normalizedValue <= 0.5) {
          return interpolateColor(COLORS.min, COLORS.mid, normalizedValue * 2);
        } else {
          return interpolateColor(COLORS.mid, COLORS.max, (normalizedValue - 0.5) * 2);
        }
      case 'count':
        // For counts: scale relative to the maximum value in the dataset
        const maxValue = fieldMaxValues[field];
        if (maxValue === undefined || maxValue === 0) return undefined;
        normalizedValue = value / maxValue;
        // Return white for zero, interpolate from white to green for others
        if (value === 0) return COLORS.none;
        return interpolateColor(COLORS.none, COLORS.max, normalizedValue);
      case 'differential':
        // For StPosDiff: centered at 0, red for negative, gray at 0, green for positive
        if (value === 0) return '#E0E0E0'; // Gray for zero
        const maxAbsValue = 20; // Maximum absolute value for scaling
        const absValue = Math.min(Math.abs(value), maxAbsValue);
        const intensity = absValue / maxAbsValue;
        
        if (value < 0) {
          // Negative values: interpolate from gray to red
          return interpolateColor('#E0E0E0', COLORS.min, intensity);
        } else {
          // Positive values: interpolate from gray to green
          return interpolateColor('#E0E0E0', COLORS.max, intensity);
        }
      case 'rating':
        // For ratings: scale based on field
        const min = 0;
        const max = value > 100 ? value * 1.1 : 100;
        normalizedValue = Math.max(0, Math.min(1, (value - min) / (max - min)));
        normalizedValue = mapping.invert ? 1 - normalizedValue : normalizedValue;
        break;
      default:
        return undefined;
    }

    if (normalizedValue <= 0.5) {
      return interpolateColor(COLORS.min, COLORS.mid, normalizedValue * 2);
    } else {
      return interpolateColor(COLORS.mid, COLORS.max, (normalizedValue - 0.5) * 2);
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    
    if (over && active.id !== over.id) {
      setLoopDataColumns((prevColumns) => {
        const oldIndex = prevColumns.findIndex((col) => col.id === active.id);
        const newIndex = prevColumns.findIndex((col) => col.id === over.id);
        return arrayMove(prevColumns, oldIndex, newIndex);
      });
    }
  };

  // Filter loop data based on visible drivers and minimum races
  const filteredLoopData = useMemo(() => {
    return loopData.filter(data => {
      const totalRaces = Number(data.TotalRaces || data.Total || 0);
      
      return visibleDrivers.has(data.Driver) && totalRaces >= minRacesFilter;
    });
  }, [loopData, visibleDrivers, minRacesFilter]);

  // Memoize max values for each field to avoid recalculating in every cell
  const fieldMaxValues = useMemo(() => {
    if (!filteredLoopData || filteredLoopData.length === 0) return {};
    
    const maxValues: Record<string, number> = {};
    Object.keys(loopDataColorMapping).forEach(field => {
      if (loopDataColorMapping[field].type === 'count') {
        maxValues[field] = Math.max(...filteredLoopData.map(d => Number(d[field] || 0)));
      }
    });
    return maxValues;
  }, [filteredLoopData]);

  // Sort loop data based on selected column
  const sortedLoopData = useMemo(() => {
    if (!filteredLoopData) return [];
    
    return [...filteredLoopData].sort((a, b) => {
      if (loopDataSort === 'Driver') {
        return loopDataSortDirection === 'asc' 
          ? a.Driver.localeCompare(b.Driver)
          : b.Driver.localeCompare(a.Driver);
      }
      
      const aValue = Number(a[loopDataSort]);
      const bValue = Number(b[loopDataSort]);
      
      return loopDataSortDirection === 'asc' ? aValue - bValue : bValue - aValue;
    });
  }, [filteredLoopData, loopDataSort, loopDataSortDirection]);

  const handleSortChange = (event: SelectChangeEvent) => {
    setLoopDataSort(event.target.value);
  };

  const handleDirectionChange = () => {
    setLoopDataSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
  };

  const handleColumnSort = (columnId: string) => {
    if (loopDataSort === columnId) {
      // If clicking the same column, toggle direction
      setLoopDataSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      // If clicking a different column, set it as the new sort field and default to desc for most columns
      setLoopDataSort(columnId);
      // Default to descending for most columns, ascending for Driver name
      setLoopDataSortDirection(columnId === 'Driver' ? 'asc' : 'desc');
    }
  };

  const handleLoopDataRowClick = (driverName: string) => {
    setSelectedDrivers([{
      driverName,
      trackNames: uniqueTracks,
      year: predictions[0].year,
    }]);
  };

  const handleDriverCheckboxChange = (driverName: string) => {
    setCheckedDrivers(prev => {
      const newChecked = new Set(prev);
      if (newChecked.has(driverName)) {
        newChecked.delete(driverName);
      } else {
        newChecked.add(driverName);
      }
      return newChecked;
    });
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (!predictions || predictions.length === 0) {
    return null;
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'flex-end',
        gap: 2, 
        mb: 2, 
        alignItems: 'center',
        borderBottom: 1,
        borderColor: 'divider',
        pb: 2
      }}>
        <Typography variant="body2" color="text.secondary" sx={{ mr: 'auto' }}>
          {allDrivers.length} Drivers • {uniqueTracks.length} Tracks
        </Typography>

        <FormControl sx={{ minWidth: 200 }} size="small">
          <Autocomplete
            multiple
            id="driver-filter"
            options={['Select All', ...allDrivers]}
            value={Array.from(visibleDrivers)}
            onChange={(event, newValue) => {
              // Handle Select All option
              if (newValue.includes('Select All')) {
                if (visibleDrivers.size === allDrivers.length) {
                  // If all were selected, deselect all
                  setVisibleDrivers(new Set());
                  setExpanded(new Set());
                  setSelectedDrivers([]);
                } else {
                  // Select all drivers
                  setVisibleDrivers(new Set(allDrivers));
                }
                return;
              }

              setVisibleDrivers(new Set(newValue));
              // Clear expanded and selected state for hidden drivers
              setExpanded(prev => {
                const newExpanded = new Set(prev);
                Array.from(prev).forEach(driverName => {
                  if (!newValue.includes(driverName)) {
                    newExpanded.delete(driverName);
                  }
                });
                return newExpanded;
              });
              setSelectedDrivers(prev => prev.filter(d => newValue.includes(d.driverName)));
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={`Drivers (${visibleDrivers.size}/${allDrivers.length})`}
                placeholder="Search drivers..."
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.23)' : 'rgba(0, 0, 0, 0.23)',
                    },
                    '&:hover fieldset': {
                      borderColor: (theme) => theme.palette.mode === 'dark' ? '#fff' : '#000',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: (theme) => theme.palette.mode === 'dark' ? '#fff' : '#000',
                    },
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: (theme) => theme.palette.mode === 'dark' ? '#fff' : '#000',
                  },
                }}
              />
            )}
            renderTags={(value: string[], getTagProps) =>
              value.length === allDrivers.length ? (
                <Chip 
                  label="All Drivers" 
                  size="small"
                  sx={{
                    backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)',
                    color: 'text.primary',
                  }}
                />
              ) : value.length > 0 ? (
                <Chip 
                  label={`${value.length} Drivers Selected`} 
                  size="small"
                  sx={{
                    backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)',
                    color: 'text.primary',
                  }}
                />
              ) : null
            }
            limitTags={1}
            disableCloseOnSelect
            renderOption={(props, option, { selected }) => {
              if (option === 'Select All') {
                return (
                  <MenuItem
                    {...props}
                    key={option}
                    value={option}
                    sx={{
                      '&:hover': {
                        backgroundColor: (theme) => 
                          theme.palette.mode === 'dark' 
                            ? 'rgba(255, 255, 255, 0.08)'
                            : 'rgba(0, 0, 0, 0.04)',
                      },
                    }}
                  >
                    <Checkbox 
                      checked={visibleDrivers.size === allDrivers.length}
                      indeterminate={visibleDrivers.size > 0 && visibleDrivers.size < allDrivers.length}
                      sx={{
                        color: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)',
                        '&.Mui-checked, &.MuiCheckbox-indeterminate': {
                          color: (theme) => theme.palette.mode === 'dark' ? '#fff' : '#000',
                        },
                      }}
                    />
                    <ListItemText primary={option} />
                  </MenuItem>
                );
              }
              return (
                <MenuItem
                  {...props}
                  key={option}
                  value={option}
                  sx={{
                    '&:hover': {
                      backgroundColor: (theme) => 
                        theme.palette.mode === 'dark' 
                          ? 'rgba(255, 255, 255, 0.08)'
                          : 'rgba(0, 0, 0, 0.04)',
                    },
                  }}
                >
                  <Checkbox 
                    checked={selected}
                    sx={{
                      color: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)',
                      '&.Mui-checked': {
                        color: (theme) => theme.palette.mode === 'dark' ? '#fff' : '#000',
                      },
                    }}
                  />
                  <ListItemText primary={option} />
                </MenuItem>
              );
            }}
            getOptionDisabled={(option) => option === 'Select All' && visibleDrivers.size === allDrivers.length}
            sx={{
              '& .MuiAutocomplete-clearIndicator, & .MuiAutocomplete-popupIndicator': {
                color: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)',
                '&:hover': {
                  color: (theme) => theme.palette.mode === 'dark' ? '#fff' : '#000',
                },
              },
            }}
          />
        </FormControl>
      </Box>


      <TabPanel value={tabValue} index={0}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={handleCollapseAll}
            disabled={expanded.size === 0}
            sx={{ 
              backgroundColor: 'background.paper',
              color: (theme) => theme.palette.mode === 'dark' ? '#fff' : '#000',
              borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.23)' : 'rgba(0, 0, 0, 0.23)',
              '&:hover': {
                backgroundColor: (theme) => 
                  theme.palette.mode === 'dark' 
                    ? 'rgba(255, 255, 255, 0.08)'
                    : 'rgba(0, 0, 0, 0.04)',
                borderColor: (theme) => theme.palette.mode === 'dark' ? '#fff' : '#000',
              },
              '&.Mui-disabled': {
                color: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)',
                borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)',
              },
              mr: 2,
            }}
          >
            {expanded.size === 0 ? 'Select drivers to expand' : 'Collapse All'}
          </Button>

          <OddsToggle checked={showAmericanOdds} onChange={setShowAmericanOdds} />
        </Box>

        <TableContainer 
          component={Paper}
          sx={{
            overflowX: 'auto',
            width: isMobile ? 'calc(100vw - 16px)' : 'auto',
            marginLeft: isMobile ? '8px' : 0,
            marginRight: isMobile ? '8px' : 0,
            '&::-webkit-scrollbar': {
              height: '8px',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)',
              borderRadius: '4px',
            },
          }}
        >
          <Table 
            size="small" 
            sx={{ 
              minWidth: isMobile ? '400px' : 'auto',
              '& .MuiTableCell-root': {
                padding: isMobile ? '4px 6px' : undefined,
              },
            }}
          >
            <TableHead>
              <TableRow sx={{ backgroundColor: 'background.paper' }}>
                <TableCell sx={{ width: isMobile ? '35px' : '60px', py: 0.5 }}>
                  <TableSortLabel
                    active={orderBy === 'rank'}
                    direction={orderBy === 'rank' ? order : 'asc'}
                    onClick={() => handleRequestSort('rank')}
                    sx={{ fontWeight: 'bold' }}
                  >
                    Rank
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ width: isMobile ? '70px' : 'auto', py: 0.5 }}>
                  <TableSortLabel
                    active={orderBy === 'driverName'}
                    direction={orderBy === 'driverName' ? order : 'asc'}
                    onClick={() => handleRequestSort('driverName')}
                    sx={{ fontWeight: 'bold' }}
                  >
                    Driver
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right" sx={{ width: isMobile ? '60px' : 'auto', py: 0.5 }}>
                  <TableSortLabel
                    active={orderBy === 'predictedPosition'}
                    direction={orderBy === 'predictedPosition' ? order : 'asc'}
                    onClick={() => handleRequestSort('predictedPosition')}
                    sx={{ fontWeight: 'bold', whiteSpace: 'pre-line', lineHeight: isMobile ? 1.2 : 'normal' }}
                  >
                    {isMobile ? 'Predicted\nPosition' : 'Predicted Position'}
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right" sx={{ width: isMobile ? '60px' : 'auto', py: 0.5 }}>
                  <TableSortLabel
                    active={orderBy === 'top10Probability'}
                    direction={orderBy === 'top10Probability' ? order : 'asc'}
                    onClick={() => handleRequestSort('top10Probability')}
                    sx={{ fontWeight: 'bold' }}
                  >
                    Top 10 {showAmericanOdds ? "Odds" : "%"}
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right" sx={{ width: isMobile ? '60px' : 'auto', py: 0.5 }}>
                  <TableSortLabel
                    active={orderBy === 'top5Probability'}
                    direction={orderBy === 'top5Probability' ? order : 'asc'}
                    onClick={() => handleRequestSort('top5Probability')}
                    sx={{ fontWeight: 'bold' }}
                  >
                    Top 5 {showAmericanOdds ? "Odds" : "%"}
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right" sx={{ width: isMobile ? '60px' : 'auto', py: 0.5 }}>
                  <TableSortLabel
                    active={orderBy === 'winProbability'}
                    direction={orderBy === 'winProbability' ? order : 'asc'}
                    onClick={() => handleRequestSort('winProbability')}
                    sx={{ fontWeight: 'bold' }}
                  >
                    Win {showAmericanOdds ? "Odds" : "%"}
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedAndFilteredPredictions.map((prediction, index) => (
                <React.Fragment key={prediction.driverName}>
                  <TableRow 
                    hover
                    onClick={() => handleExpandClick(prediction.driverName)}
                    sx={{ 
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                      },
                    }}
                  >
                    <TableCell sx={{ fontWeight: 'bold', color: 'text.secondary', width: isMobile ? '35px' : '60px', py: 0.5 }}>
                      <IconButton
                        aria-label="expand row"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleExpandClick(prediction.driverName);
                        }}
                      >
                        {expanded.has(prediction.driverName) ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                      </IconButton>
                      {index + 1}
                    </TableCell>
                    <TableCell component="th" scope="row" sx={{ fontWeight: 'medium', width: isMobile ? '70px' : 'auto', py: 0.5 }}>
                      {prediction.driverName}
                    </TableCell>
                    <TableCell align="right" sx={{ backgroundColor: getCellColor(prediction.predictedPosition, 'position'), color: 'black', fontWeight: 'medium', transition: 'all 0.2s', width: isMobile ? '60px' : 'auto', py: 0.5 }}>
                      {prediction.predictedPosition.toFixed(1)}
                    </TableCell>
                    <TableCell align="right" sx={{ backgroundColor: getCellColor(prediction.top10Probability, 'probability'), color: 'black', fontWeight: 'medium', transition: 'all 0.2s', width: isMobile ? '60px' : 'auto', py: 0.5 }}>
                      {showAmericanOdds 
                        ? toAmericanOdds(prediction.top10Probability)
                        : `${(prediction.top10Probability * 100).toFixed(1)}%`
                      }
                    </TableCell>
                    <TableCell align="right" sx={{ backgroundColor: getCellColor(prediction.top5Probability, 'probability'), color: 'black', fontWeight: 'medium', transition: 'all 0.2s', width: isMobile ? '60px' : 'auto', py: 0.5 }}>
                      {showAmericanOdds 
                        ? toAmericanOdds(prediction.top5Probability)
                        : `${(prediction.top5Probability * 100).toFixed(1)}%`
                      }
                    </TableCell>
                    <TableCell align="right" sx={{ backgroundColor: getCellColor(prediction.winProbability, 'probability'), color: 'black', fontWeight: 'medium', transition: 'all 0.2s', width: isMobile ? '60px' : 'auto', py: 0.5 }}>
                      {showAmericanOdds 
                        ? toAmericanOdds(prediction.winProbability)
                        : `${(prediction.winProbability * 100).toFixed(1)}%`
                      }
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
                      <Collapse in={expanded.has(prediction.driverName)} timeout="auto" unmountOnExit>
                        <Box 
                          sx={{ 
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            py: 1,
                            px: 2,
                          }}
                          onClick={() => handleExpandedRowClick(prediction)}
                          style={{ cursor: 'pointer' }}
                        >
                          {Object.entries(statLabels).map(([key, label]) => (
                            <Box
                              key={key}
                              sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: 0.5,
                              }}
                            >
                              <Typography 
                                variant="caption" 
                                sx={{ 
                                  fontSize: '0.7rem',
                                  color: 'text.secondary',
                                  fontWeight: 500,
                                }}
                              >
                                {label}
                              </Typography>
                              <Box
                                sx={{
                                  px: 2,
                                  py: 0.5,
                                  borderRadius: 1,
                                  bgcolor: getBadgeColor(prediction[key as StatKey], key),
                                  minWidth: '60px',
                                  textAlign: 'center',
                                  '&:hover': {
                                    opacity: 0.9,
                                  },
                                }}
                              >
                                <Typography 
                                  variant="body2" 
                                  sx={{ 
                                    fontSize: '0.8rem',
                                    fontWeight: 'bold',
                                    color: 'black',
                                  }}
                                >
                                  {key === 'top15Pct' 
                                    ? `${Math.round(prediction[key as StatKey])}%`
                                    : key === 'avgRating'
                                      ? Math.round(prediction[key as StatKey])
                                      : prediction[key as StatKey].toFixed(1)
                                  }
                                </Typography>
                              </Box>
                            </Box>
                          ))}
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
                <Box sx={{ 
                  display: 'flex', 
                  gap: 2, 
                  mb: 2, 
                  alignItems: 'center', 
                  flexWrap: 'wrap',
                  flexDirection: isMobile ? 'column' : 'row',
                  '& > *': {
                    width: isMobile ? '100%' : 'auto',
                  }
                }}>
          <FormControl size="small" sx={{ minWidth: isMobile ? '100%' : 200 }}>
            <InputLabel>Sort By</InputLabel>
            <Select
              value={loopDataSort}
              label="Sort By"
              onChange={handleSortChange}
            >
              {loopDataColumns.map((column) => (
                <MenuItem key={column.id} value={column.id}>
                  {column.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <IconButton onClick={handleDirectionChange} size="small">
            {loopDataSortDirection === 'asc' ? <ArrowUpward /> : <ArrowDownward />}
          </IconButton>
          <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
              Min races:
            </Typography>
            {minRacesFilter > 0 && (
              <IconButton
                size="small"
                onClick={() => setMinRacesFilter(0)}
                sx={{ 
                  p: 0.5, 
                  minWidth: 20, 
                  height: 20,
                  borderRadius: 1,
                  backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
                  '&:hover': {
                    backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.08)',
                  }
                }}
              >
                <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>×</Typography>
              </IconButton>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <IconButton
                size="small"
                onClick={() => setMinRacesFilter(Math.max(0, minRacesFilter - 1))}
                disabled={minRacesFilter <= 0}
                sx={{ 
                  p: 0.5, 
                  minWidth: 24, 
                  height: 24,
                  borderRadius: 1,
                  backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
                  '&:hover': {
                    backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.08)',
                  },
                  '&:disabled': {
                    opacity: 0.4,
                  }
                }}
              >
                <Typography variant="caption" sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>-</Typography>
              </IconButton>
              <Typography 
                variant="body2" 
                sx={{ 
                  minWidth: 20, 
                  textAlign: 'center', 
                  fontWeight: 'bold',
                  color: minRacesFilter > 0 ? 'primary.main' : 'text.secondary'
                }}
              >
                {minRacesFilter}
              </Typography>
              <IconButton
                size="small"
                onClick={() => setMinRacesFilter(minRacesFilter + 1)}
                sx={{ 
                  p: 0.5, 
                  minWidth: 24, 
                  height: 24,
                  borderRadius: 1,
                  backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
                  '&:hover': {
                    backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.08)',
                  }
                }}
              >
                <Typography variant="caption" sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>+</Typography>
              </IconButton>
            </Box>
          </Box>
        </Box>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Showing {filteredLoopData.length} drivers with {minRacesFilter > 0 ? `at least ${minRacesFilter} races` : 'any number of races'} • Total drivers: {loopData.length}
          </Typography>
        </Box>
        <TableContainer 
          component={Paper} 
          sx={{ 
            maxHeight: '70vh',
            overflowX: 'auto',
            overflowY: 'auto',
            width: isMobile ? '100vw' : 'auto',
            marginLeft: isMobile ? '-24px' : 0,
            marginRight: isMobile ? '-24px' : 0,
            '&::-webkit-scrollbar': {
              height: '12px',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.04)',
              borderRadius: '6px',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)',
              borderRadius: '6px',
              '&:hover': {
                backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)',
              },
            },
          }}
        >
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <Table 
              size="small" 
              sx={{ 
                minWidth: isMobile ? '800px' : 'auto',
                '& .MuiTableCell-root': {
                  padding: isMobile ? '4px 6px' : undefined,
                },
              }}
            >
              <TableHead
                sx={{
                  position: 'sticky',
                  top: 0,
                  zIndex: 3,
                  backgroundColor: 'background.paper',
                }}
              >
                <TableRow>
                  {!isMobile && (
                    <TableCell 
                      sx={{ 
                        fontWeight: 'bold',
                        width: '60px',
                        backgroundColor: 'background.paper',
                        position: 'sticky',
                        left: 0,
                        zIndex: 4,
                        borderRight: '1px solid rgba(224, 224, 224, 1)',
                      }}
                    >
                      Select
                    </TableCell>
                  )}
                  <TableCell 
                    sx={{ 
                      fontWeight: 'bold',
                      width: isMobile ? '30px' : '60px',
                      backgroundColor: 'background.paper',
                      position: 'sticky',
                      left: isMobile ? 0 : '60px',
                      zIndex: 4,
                      borderRight: '1px solid rgba(224, 224, 224, 1)',
                    }}
                  >
                    {isMobile ? '' : 'Rank'}
                  </TableCell>
                  <SortableContext
                    items={loopDataColumns.map(col => col.id)}
                    strategy={horizontalListSortingStrategy}
                  >
                    {loopDataColumns.map((column) => (
                      <SortableHeader 
                        key={column.id} 
                        column={column} 
                        sortField={loopDataSort}
                        sortDirection={loopDataSortDirection}
                        onSort={handleColumnSort}
                        isMobile={isMobile}
                      />
                    ))}
                  </SortableContext>
                </TableRow>
              </TableHead>
              <TableBody>
                {loopDataLoading ? (
                  <TableRow>
                    <TableCell colSpan={loopDataColumns.length + (isMobile ? 1 : 2)} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : (
                  sortedLoopData.map((data, index) => {
                    const isChecked = checkedDrivers.has(data.Driver);
                    const hasCheckedDrivers = checkedDrivers.size > 0;
                    const shouldGrayOut = hasCheckedDrivers && !isChecked;
                    
                    return (
                      <TableRow 
                        key={data.Driver}
                        onClick={() => handleLoopDataRowClick(data.Driver)}
                        sx={{ 
                          cursor: 'pointer',
                          opacity: shouldGrayOut ? 0.4 : 1,
                          '&:hover': {
                            backgroundColor: (theme) => 
                              theme.palette.mode === 'dark' 
                                ? 'rgba(255, 255, 255, 0.08)'
                                : 'rgba(0, 0, 0, 0.04)',
                          },
                        }}
                      >
                        {!isMobile && (
                          <TableCell 
                            sx={{ 
                              backgroundColor: 'background.paper',
                              position: 'sticky',
                              left: 0,
                              zIndex: 1,
                              borderRight: '1px solid rgba(224, 224, 224, 1)',
                            }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Checkbox
                              checked={isChecked}
                              onChange={() => handleDriverCheckboxChange(data.Driver)}
                              size="small"
                            />
                          </TableCell>
                        )}
                        <TableCell 
                          sx={{ 
                            backgroundColor: 'background.paper',
                            width: isMobile ? '30px' : '60px',
                            fontWeight: 'medium',
                            position: 'sticky',
                            left: isMobile ? 0 : '60px',
                            zIndex: 1,
                            borderRight: '1px solid rgba(224, 224, 224, 1)',
                          }}
                        >
                          {index + 1}
                        </TableCell>
                        {loopDataColumns.map((column) => (
                          <TableCell
                            key={column.id}
                            align={column.id === 'Driver' ? 'left' : 'right'}
                            sx={{
                              ...(column.id === 'Driver' ? {
                                backgroundColor: 'background.paper',
                                position: 'sticky',
                                left: isMobile ? '30px' : '120px',
                                zIndex: 1,
                                borderRight: '1px solid rgba(224, 224, 224, 1)',
                                fontWeight: 'bold',
                              } : {
                                backgroundColor: getLoopDataCellColor(Number(data[column.id]), column.id),
                                color: 'black',
                                fontWeight: 'medium',
                                py: 0.5,
                              })
                            }}
                          >
                            {column.id === 'Driver' ? data[column.id] :
                             column.id === 'PctQPass' || column.id === 'PctT15Lap' || column.id === 'PctLed' || 
                             column.id === 'WinPct' || column.id === 'Top5Pct' || column.id === 'Top10Pct'
                               ? `${(Number(data[column.id] || 0)).toFixed(1)}%` :
                             column.id === 'DriverRating' || column.id === 'Avg' || column.id === 'Pos' || column.id === 'StPosDiff'
                               ? (Number(data[column.id] || 0)).toFixed(1) :
                             column.id === 'Wins' || column.id === 'Top5s' || column.id === 'Top10s'
                               ? Math.round(Number(data[column.id] || 0)) :
                             Math.round(Number(data[column.id] || 0))}
                          </TableCell>
                        ))}
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </DndContext>
        </TableContainer>
      </TabPanel>

      <DriverDetailsModal
        open={selectedDrivers.length > 0}
        onClose={handleCloseModal}
        driversDetails={driversDetails || {}}
        isLoading={isDriverDetailsLoading}
      />
    </Box>
  );
}; 